import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TextArea } from "~/features/settings/controls";

/**
 * A JSON object, edited as text, parsed on blur. Shared by the policy form's `conditions`
 * field — invalid JSON shows an inline error and never reaches the request.
 */
export function ConditionsEditor({
  value,
  onChange,
  label,
}: {
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  label: string;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(() => JSON.stringify(value, null, 2));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(JSON.stringify(value, null, 2));
  }, [value]);

  const commit = () => {
    const text = draft.trim();
    if (!text) {
      setError(null);
      onChange({});
      setDraft("{}");
      return;
    }
    try {
      const parsed = JSON.parse(text) as unknown;
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        setError(t("Must be a JSON object, e.g. {}"));
        return;
      }
      setError(null);
      onChange(parsed as Record<string, unknown>);
    } catch {
      setError(t("Not valid JSON"));
    }
  };

  return (
    <TextArea
      label={label}
      hint={error ?? t('Example: {"resource.sensitivity": {"eq": 1}}')}
      rows={4}
      className="font-mono"
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      aria-invalid={error ? true : undefined}
    />
  );
}
